import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaPaintBrush, FaCheck, FaTimes, FaUndo, FaSpinner } from 'react-icons/fa';

const ImageAnnotationModal = ({ imageFile, onSave, onCancel, isUploading: externalIsUploading }) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushColor, setBrushColor] = useState('#000000');
  const [brushSize, setBrushSize] = useState(5);
  const [history, setHistory] = useState([]);
  const [historyStep, setHistoryStep] = useState(-1);
  const [isUploading, setIsUploading] = useState(false);
  const lastPointRef = useRef(null);
  const pointsRef = useRef([]);
  
  // Use external uploading state if provided, otherwise use internal state
  const uploading = externalIsUploading !== undefined ? externalIsUploading : isUploading;

  useEffect(() => {
    if (!imageFile || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Set canvas size to match image (but limit max size for display)
      const maxDisplayWidth = 1200;
      const maxDisplayHeight = 800;
      let displayWidth = img.width;
      let displayHeight = img.height;

      if (img.width > maxDisplayWidth || img.height > maxDisplayHeight) {
        const scale = Math.min(maxDisplayWidth / img.width, maxDisplayHeight / img.height);
        displayWidth = img.width * scale;
        displayHeight = img.height * scale;
      }

      canvas.width = displayWidth;
      canvas.height = displayHeight;
      
      // Draw image on canvas
      ctx.drawImage(img, 0, 0, displayWidth, displayHeight);
      
      // Save initial state to history
      setHistory([{ 
        dataUrl: canvas.toDataURL(), 
        width: displayWidth, 
        height: displayHeight 
      }]);
      setHistoryStep(0);
      lastPointRef.current = null;
    };

    img.onerror = () => {
      console.error('Failed to load image');
    };

    img.src = URL.createObjectURL(imageFile);
    imageRef.current = img;

    return () => {
      if (img.src) {
        URL.revokeObjectURL(img.src);
      }
    };
  }, [imageFile]);

  const getCanvasCoordinates = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  }, []);

  const startDrawing = useCallback((e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const { x, y } = getCanvasCoordinates(e);
    
    const ctx = canvas.getContext('2d');
    
    // Set drawing properties
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.globalCompositeOperation = 'source-over';
    ctx.strokeStyle = brushColor;
    
    // Start new path and reset points array
    ctx.beginPath();
    ctx.moveTo(x, y);
    lastPointRef.current = { x, y };
    pointsRef.current = [{ x, y }];
  }, [brushColor, brushSize, getCanvasCoordinates]);

  const draw = useCallback((e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const { x, y } = getCanvasCoordinates(e);
    
    const ctx = canvas.getContext('2d');
    
    // Set properties for smooth drawing
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.globalCompositeOperation = 'source-over';
    ctx.strokeStyle = brushColor;
    
    // Draw line from last point to current point to prevent gaps
    if (lastPointRef.current) {
      ctx.beginPath();
      ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
      ctx.lineTo(x, y);
      ctx.stroke();
    }
    
    // Update last point
    lastPointRef.current = { x, y };
  }, [isDrawing, brushColor, brushSize, getCanvasCoordinates]);

  const stopDrawing = useCallback(() => {
    if (isDrawing) {
      setIsDrawing(false);
      lastPointRef.current = null;
      pointsRef.current = [];
      
      // Save state to history
      const canvas = canvasRef.current;
      if (canvas) {
        const newState = {
          dataUrl: canvas.toDataURL(),
          width: canvas.width,
          height: canvas.height
        };
        setHistory((prevHistory) => {
          const newHistory = prevHistory.slice(0, historyStep + 1);
          newHistory.push(newState);
          return newHistory;
        });
        setHistoryStep((prevStep) => prevStep + 1);
      }
    }
  }, [isDrawing, historyStep]);

  const undo = useCallback(() => {
    if (historyStep > 0 && history.length > 0) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      const previousState = history[historyStep - 1];
      const img = new Image();
      
      img.onload = () => {
        // Restore canvas dimensions
        canvas.width = previousState.width;
        canvas.height = previousState.height;
        
        // Clear and redraw
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        setHistoryStep((prevStep) => prevStep - 1);
        lastPointRef.current = null;
      };
      
      img.onerror = () => {
        console.error('Failed to load image for undo');
      };
      
      img.src = previousState.dataUrl;
    }
  }, [history, historyStep]);

  const handleSave = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || uploading) return;
    
    if (externalIsUploading === undefined) {
      setIsUploading(true);
    }
    
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], imageFile.name, { type: imageFile.type });
        // Call onSave - parent component handles the actual upload
        onSave(file);
      } else {
        console.error('Failed to create blob from canvas');
        if (externalIsUploading === undefined) {
          setIsUploading(false);
        }
      }
    }, imageFile.type || 'image/png', 0.95);
  }, [imageFile, onSave, uploading, externalIsUploading]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    startDrawing(e);
  }, [startDrawing]);

  const handleMouseMove = useCallback((e) => {
    e.preventDefault();
    draw(e);
  }, [draw]);

  const handleMouseUp = useCallback(() => {
    stopDrawing();
  }, [stopDrawing]);

  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    startDrawing(e);
  }, [startDrawing]);

  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
    draw(e);
  }, [draw]);

  const handleTouchEnd = useCallback(() => {
    stopDrawing();
  }, [stopDrawing]);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center shadow-md">
              <FaPaintBrush className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Image Editor
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Draw and annotate your image
              </p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
            aria-label="Close"
          >
            <FaTimes className="w-5 h-5 text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-100" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-wrap">
          {/* Color Picker */}
          <div className="flex items-center gap-2 border-r border-gray-200 dark:border-gray-700 pr-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Color:</label>
            <div className="relative">
              <input
                type="color"
                value={brushColor}
                onChange={(e) => setBrushColor(e.target.value)}
                className="w-10 h-10 rounded-lg border-2 border-gray-300 dark:border-gray-600 cursor-pointer hover:border-blue-500 transition-colors"
                aria-label="Brush color"
              />
              <div 
                className="absolute inset-0 rounded-lg pointer-events-none border-2 border-white dark:border-gray-800"
                style={{ backgroundColor: brushColor }}
              />
            </div>
          </div>
          
          {/* Brush Size */}
          <div className="flex items-center gap-3 border-r border-gray-200 dark:border-gray-700 pr-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">Brush Size:</label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="1"
                max="50"
                value={brushSize}
                onChange={(e) => setBrushSize(Number(e.target.value))}
                className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                aria-label="Brush size"
              />
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 w-10 text-right">{brushSize}px</span>
            </div>
          </div>

          {/* Undo */}
          <div className="border-r border-gray-200 dark:border-gray-700 pr-3">
            <button
              onClick={undo}
              disabled={historyStep <= 0}
              className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-2 font-medium"
              title="Undo Last Action"
              aria-label="Undo"
            >
              <FaUndo className="w-4 h-4" />
              <span className="text-sm">Undo</span>
            </button>
          </div>

          <div className="flex-1" />

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={onCancel}
              disabled={uploading}
              className="px-5 py-2.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (uploading) return;
                if (externalIsUploading === undefined) {
                  setIsUploading(true);
                }
                // Upload original image without annotation
                onSave(imageFile);
              }}
              disabled={uploading}
              className="px-5 py-2.5 rounded-lg bg-gray-500 text-white hover:bg-gray-600 transition-all font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <>
                  <FaSpinner className="w-4 h-4 animate-spin inline mr-2" />
                  Uploading...
                </>
              ) : (
                'Skip & Upload'
              )}
            </button>
            <button
              onClick={handleSave}
              disabled={uploading}
              className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 transition-all flex items-center gap-2 font-medium shadow-lg shadow-blue-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <>
                  <FaSpinner className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <FaCheck className="w-4 h-4" />
                  Save & Upload
                </>
              )}
            </button>
          </div>
        </div>

        {/* Canvas Container */}
        <div className="flex-1 overflow-auto p-6 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 relative">
          {/* Loading Overlay */}
          {uploading && (
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 rounded-b-2xl">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-2xl flex flex-col items-center gap-4">
                <FaSpinner className="w-12 h-12 text-blue-500 animate-spin" />
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Uploading image...
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Please wait while we process your image
                </p>
              </div>
            </div>
          )}
          
          <div className="flex items-center justify-center relative min-h-full">
            <div className="relative inline-block shadow-2xl rounded-xl overflow-hidden border-4 border-white dark:border-gray-700">
              <canvas
                ref={canvasRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                className="max-w-full max-h-[calc(95vh-280px)] cursor-crosshair"
                style={{ touchAction: 'none', display: 'block' }}
                aria-label="Drawing canvas"
                disabled={uploading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageAnnotationModal;
